import type { CSSProperties, ReactNode } from 'react';

export interface ScreenProps {
  children: ReactNode;
  /** Vertically centres the content — used by the auth and setup screens. */
  center?: boolean;
  maxWidth?: number;
  style?: CSSProperties;
}

/** Page shell: safe-area padding and a centred max-width column. */
export function Screen({ children, center = false, maxWidth = 460, style }: ScreenProps) {
  return (
    <div
      style={{
        minHeight: '100%',
        // Deliberately transparent. html paints --bg, and the leaf texture in
        // index.css sits on a fixed layer between that and #root — an opaque
        // background here is a full-height sheet directly on top of it, which
        // is exactly how the texture managed to render and still be invisible
        // on every screen.
        background: 'transparent',
        display: 'flex',
        justifyContent: 'center',
        alignItems: center ? 'center' : 'flex-start',
        padding: 'max(var(--sp-6), env(safe-area-inset-top)) var(--sp-4) var(--sp-10)',
        ...style,
      }}
    >
      <div style={{ width: '100%', maxWidth }}>{children}</div>
    </div>
  );
}

export function Stack({
  children,
  gap = 4,
  style,
}: {
  children: ReactNode;
  gap?: 1 | 2 | 3 | 4 | 5 | 6 | 8;
  style?: CSSProperties;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: `var(--sp-${gap})`, ...style }}>
      {children}
    </div>
  );
}
